// islands.ts — object-card ISLAND placeholders + hydration (OC-1 of
// plans/retire-php-frontend-render-execution-plan.md, [171] directive 3).
//
// DB-backed regions retain raw mounts or server fragments. Book/gather action decisions
// render here from saved object/context facts; the activity bridges own execution.
// The TS card emits a small placeholder where each island belongs; this module fetches the
// island's HTML fragment from the server endpoint (options/tables/object_card_island_inc.php
// — which includes the SAME partial the PHP chrome renders) and slots it in. Link visibility
// is projected in the browser from the complete saved relationship set.
//
// Wire format mirrors queryData (POST JSON body → PHP get_data()). Most placeholders are
// intentionally empty; encounter links paint their frame + loader immediately because resolving
// explored/permission-gated destinations can take long enough to otherwise look broken.
// Runs from BOTH render paths — the ?oc_ts mount (page + AJAX drawer) and the cache-first
// side-drawer render — via the renderObjectCard hydration pass (index.ts).

import type { CachedItemNode } from './find-cached-item';
import { projectEncounterLinkFragment, resolveEncounterLinkDisplay } from './encounter-links-display';
import { hydrateEncounterLinkZones } from '../shared/encounter-link-zone';
import { ftMountWrittenDescriptions } from './written-description';
import { encounterCreatureField, inventoryField } from '../shared/array-types';
import { hasBookReadingMechanics, isBookPcCaster, objectCardGatherKind } from '../janus-engine/display/object-card-actions-21';
import { nodeHasStatusItemId, INTEGRATED_OBJECT_ITEM_ID } from './find-cached-item';

const ISLAND_ENDPOINT = 'options/tables/object_card_island_inc.php';

type IslandWindow = Window & {
    getSidelogContext?: () => unknown;
    ftGetActiveCasterId?: () => string | null;
    ftSeedCasterFromEncounterCache?: (id: string) => boolean;
    fetchAndCacheCaster?: (id: string) => Promise<unknown>;
};

function recordOf(value: unknown): CachedItemNode | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CachedItemNode
        : null;
}

export function gatherControlKind(blob: unknown): 'gather' | 'dig' | null {
    return objectCardGatherKind(recordOf(blob), nodeHasStatusItemId(blob, 'target', INTEGRATED_OBJECT_ITEM_ID));
}

/** Existing global bridges are owned by their bundles; no stateful cross-bundle imports. */
export function bookActionsHtml(book: unknown, caster: unknown): string {
    if (!hasBookReadingMechanics(book)) return '';
    const id = String(inventoryField(book, 'inventory_id') ?? '');
    const reason = id ? 'Checking reading requirements…' : 'Read an inventory copy of this book.';
    const read = isBookPcCaster(caster)
        ? `<span data-ft-book-read-reason title="${escapeHtml(reason)}"><button type="button" data-ft-book-read disabled class="btn btn-info btn-sm"`
            + (id ? ` onclick="window.ftBookActivity('read', '${escapeHtml(id)}');"` : '')
            + `>${iconMap('read')}</button></span>` : '';
    return `<span${id ? ` data-ft-book-actions="${escapeHtml(id)}"` : ''}>${read}`
        + (id ? `<button type="button" data-ft-book-unread hidden class="btn btn-secondary btn-sm" onclick="window.ftBookActivity('unread', '${escapeHtml(id)}');">Unread Book</button>` : '') + '</span>';
}

export function bookActionMountHtml(book: unknown): string {
    const source = JSON.stringify({ item: book, caster: null }).replace(/</g, '\\u003c');
    return `<span class="ft-oc-book-mount"><script type="application/json" class="ft-oc-book-source">${source}</script><span class="ft-oc-book-output"></span></span>`;
}

export function gatherControlsHtml(blob: unknown, attempt: unknown): string {
    const kind = gatherControlKind(blob);
    if (!kind) return '';
    const id = escapeHtml(String(inventoryField(blob, 'inventory_id') ?? ''));
    const resume = recordOf(attempt)?.state === 'digging';
    const label = kind === 'dig' ? (attempt === undefined ? 'Excavation' : resume ? 'Continue digging' : 'Dig the site out') : 'Gather resource';
    return `<div class="d-flex align-items-center border rounded p-2 mb-2 gap-2 flex-wrap"><div class="pl-2 pr-2">`
        + `<button type="button" class="btn btn-info btn-sm" data-ft-gather-action="${kind}" title="${label}" aria-label="${label}"`
        + (kind === 'dig' ? ` id="btn_dig_uncover_${id}" onclick="openDigUncoverPanel('${id}');"` : ` onclick="useAbility('${id}');"`)
        + `>${iconMap('gather_resource')}</button></div>`
        + (kind === 'dig' && attempt !== undefined ? `<span class="small text-muted">${resume ? 'Pick your dig back up where you left it.' : 'Dig the site out. Careful work protects the find; rushing it does not.'}</span>` : '') + '</div>';
}

const bookMounts = new Map<HTMLElement, { item: unknown; caster: unknown; identity?: string; requestedId?: string }>();
const loadingCasters = new Map<string, Promise<unknown>>();
let bookTimer: ReturnType<typeof setInterval> | undefined;
function paintBookMount(mount: HTMLElement): void {
    if (!mount.isConnected) { bookMounts.delete(mount); return; }
    const state = bookMounts.get(mount)!;
    const host = window as IslandWindow;
    const fallback = recordOf(state.caster);
    const id = String(host.ftGetActiveCasterId ? host.ftGetActiveCasterId() ?? ''
        : host.getSidelogContext ? recordOf(host.getSidelogContext())?.caster_id ?? ''
            : encounterCreatureField(fallback?.encounter_creatures, 'encounter_creature_id') ?? '');
    host.ftSeedCasterFromEncounterCache?.(id);
    const caster = host.FateTide?.casterCache?.get(id) ?? host.FateTide?.casterCache?.get(id.toUpperCase())
        ?? (id.toUpperCase() === String(encounterCreatureField(fallback?.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase() ? fallback : null);
    const identity = `${id}:${isBookPcCaster(caster)}`;
    if (identity !== state.identity) {
        mount.querySelector<HTMLElement>('.ft-oc-book-output')!.innerHTML = bookActionsHtml(state.item, caster);
        state.identity = identity;
    }
    if (id && !caster && state.requestedId !== id && host.fetchAndCacheCaster && !loadingCasters.has(id)) {
        state.requestedId = id;
        const request = host.fetchAndCacheCaster(id); loadingCasters.set(id, request);
        void request.then(() => { for (const node of bookMounts.keys()) paintBookMount(node); })
            .catch(error => console.warn('book action caster load failed', error)).finally(() => loadingCasters.delete(id));
    }
}

export function mountBookActionInputs(root: ParentNode): void {
    const nodes = [...root.querySelectorAll<HTMLElement>('.ft-oc-book-mount')];
    if (root instanceof HTMLElement && root.matches('.ft-oc-book-mount')) nodes.unshift(root);
    for (const node of nodes) {
        if (!bookMounts.has(node)) {
            const input = recordOf(JSON.parse(node.querySelector('.ft-oc-book-source')?.textContent || 'null'));
            if (!input) continue;
            bookMounts.set(node, { item: input.item, caster: input.caster });
        }
        paintBookMount(node);
    }
    // The active-caster funnel has no event for a switch to an already-cached actor.
    if (bookMounts.size && !bookTimer) bookTimer = setInterval(() => {
        for (const node of bookMounts.keys()) paintBookMount(node);
        if (!bookMounts.size) { clearInterval(bookTimer); bookTimer = undefined; }
    }, 100);
}
new MutationObserver(changes => {
    for (const change of changes) for (const node of change.addedNodes) if (node instanceof HTMLElement) mountBookActionInputs(node);
}).observe(document.documentElement, { childList: true, subtree: true });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountBookActionInputs(document));
else mountBookActionInputs(document);

/**
 * Placeholder emitter used by render-object-card.ts. `island` names a server-side registry
 * key; `id` is what the endpoint should discern (inventory_id for instances, else item_id).
 * removeRowIfEmpty: when the placeholder's closest .ft-oc-action-row exists ONLY for this
 * island, an empty fragment removes the whole row (no empty bordered box).
 */
export function islandPlaceholder(island: string, id: string, removeRowIfEmpty = false, hiddenUntilHydrated = false): string {
    // A div (not span): fragments may carry block content (the encounter-links <ul> list);
    // as a flex-row child (book buttons) a div behaves identically.
    const pendingHtml = island === 'encounter_links'
        ? `<div data-ft-island-loading>
            <ul class="list-group mb-3">
                <li class="list-group-item active" aria-current="true">${iconMap('link')} Inventory Links:</li>
                <li class="list-group-item">
                    <div class="d-flex justify-content-center py-2" role="status" aria-live="polite">
                        <span class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
                        <span class="visually-hidden">Loading inventory links…</span>
                    </div>
                </li>
            </ul>
        </div>`
        : '';
    return `<div class="ft-oc-island" data-ft-island="${escapeHtml(island)}"`
        + ` data-ft-island-id="${escapeHtml(id)}"`
        + (removeRowIfEmpty ? ' data-ft-remove-row-if-empty="1"' : '')
        + (hiddenUntilHydrated ? ' hidden' : '')
        + `>${pendingHtml}</div>`;
}

// Context ids for the island request: the mount path carries the server-stamped page scope
// (_ft_object_card.ctx via ft_object_card_enrich); the cache-first drawer path falls back to
// the live sidelog context, exactly like renderInventoryControls does.
function islandCtx(blob: unknown): { caster_id: string; campaign_id: string; encounter_id: string; opened_in_side_info: boolean } {
    const enrich = recordOf(recordOf(blob)?._ft_object_card);
    const ctx = recordOf(enrich?.ctx);
    if (ctx && (ctx.caster_id || ctx.campaign_id || ctx.encounter_id || ctx.opened_in_side_info !== undefined)) {
        return {
            caster_id: String(ctx.caster_id ?? ''),
            campaign_id: String(ctx.campaign_id ?? ''),
            encounter_id: String(ctx.encounter_id ?? ''),
            opened_in_side_info: !!ctx.opened_in_side_info,
        };
    }
    let sc: CachedItemNode | null = null;
    try {
        const getSidelogContext = (window as IslandWindow).getSidelogContext;
        if (typeof getSidelogContext === 'function') sc = recordOf(getSidelogContext());
    } catch (e) { sc = null; }
    return {
        caster_id: String(sc?.caster_id ?? ''),
        campaign_id: String(sc?.campaign_id ?? ''),
        encounter_id: String(sc?.encounter_id ?? ''),
        // The cache-first render only ever paints the side-info drawer.
        opened_in_side_info: true,
    };
}

/**
 * Find + hydrate every un-hydrated island placeholder inside `container`. Safe to call
 * repeatedly (placeholders are claimed synchronously via data-ft-island-done). Fragments
 * are trimmed — a whitespace-only body counts as "island doesn't apply".
 */
export function ftHydrateObjectCardIslands(container: HTMLElement, blob: unknown): void {
    mountBookActionInputs(container);
    const nodes = container.querySelectorAll('.ft-oc-island[data-ft-island]:not([data-ft-island-done])');
    if (!nodes.length) return;
    const ctx = islandCtx(blob);
    const base = typeof BASE_URL === 'string' ? BASE_URL : '/';
    nodes.forEach((el) => {
        el.setAttribute('data-ft-island-done', '1'); // claim before the async fetch
        const island = el.getAttribute('data-ft-island') || '';
        const id = el.getAttribute('data-ft-island-id') || '';
        if (!island || !id) return;
        const projection = island === 'encounter_links' ? resolveEncounterLinkDisplay(recordOf(blob) ?? {}) : undefined;
        fetch(base + ISLAND_ENDPOINT, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                data_in_1: id,
                island,
                caster_id: ctx.caster_id,
                campaign_id: ctx.campaign_id,
                encounter_id: ctx.encounter_id,
                opened_in_side_info: ctx.opened_in_side_info ? '1' : '',
            }),
        })
            .then((r) => (r.ok ? r.text() : ''))
            .then(async (html) => {
                let body = island === 'encounter_links'
                    ? projectEncounterLinkFragment((html || '').trim(), recordOf(blob) ?? {}, await projection) : (html || '').trim();
                if (island === 'gather_controls') {
                    const source = new DOMParser().parseFromString(body, 'text/html').querySelector('.ft-oc-gather-source');
                    // Old PHP can precede the raw mount during bundle-first delivery. Ignore its
                    // eligibility/markup; unknown attempt facts keep neutral excavation wording.
                    body = gatherControlsHtml(blob, source ? JSON.parse(source.textContent || 'null') : undefined);
                }
                if (body === '') {
                    // Island doesn't apply for this item/viewer — drop the placeholder, and the
                    // whole wrapper row when it existed only for this island.
                    if (el.getAttribute('data-ft-remove-row-if-empty') === '1') {
                        const row = el.closest('.ft-oc-action-row');
                        if (row) { row.remove(); return; }
                    }
                    el.remove();
                    return;
                }
                (el as HTMLElement).innerHTML = body;
                if (island === 'book_actions') mountBookActionInputs(el);
                if (island === 'written_description') ftMountWrittenDescriptions(el);
                hydrateEncounterLinkZones(el);
                el.removeAttribute('hidden');
                // The fragments carry icon_ph() placeholders (server can't inline SVG) — upgrade
                // them. Tooltips are delegated globally (template.js body mouseover), no init needed.
                if (typeof window.ftMountIcons === 'function') {
                    try { window.ftMountIcons(el as HTMLElement); } catch (e) { /* footer pass covers */ }
                }
                // The table_body island fragment is a bare ft_table_mount() span
                // (.ft-pt-table-mount) — the async island fetch resolves AFTER index.ts's
                // one-shot ftMountParseTextTables pass, so hydrate the freshly-injected mount
                // here or the random/table body never renders in the side-info panel.
                // No later pass re-scans this subtree (the one-shot footer/container hydration
                // already ran before this async fetch resolved), so a throw here would leave the
                // island unhydrated — swallow it only to keep one bad island from killing the rest.
                if (typeof window.ftMountParseTextTables === 'function') {
                    try { window.ftMountParseTextTables(el as HTMLElement); } catch (e) { console.warn('island table-mount hydrate failed', e); }
                }
                if (typeof window.ftMountParsedText === 'function') {
                    try { window.ftMountParsedText(el as HTMLElement); } catch (e) { console.warn('island parsed-text hydrate failed', e); }
                }
            })
            .catch(() => {
                // Do not leave the encounter-links loading frame spinning forever on a failed request.
                if (island === 'encounter_links') el.remove();
            });
    });
}
